// Procedural fallback sounds: oscillators + noise. Used until real samples are processed, so the game is never silent.

/** Deterministic white noise buffer (LCG), so every run sounds the same. */
export function noiseBuffer(ctx: BaseAudioContext, seconds = 2, seed = 1): AudioBuffer {
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
  const d = buf.getChannelData(0);
  let s = seed >>> 0;
  for (let i = 0; i < d.length; i++) { s = (s * 1664525 + 1013904223) >>> 0; d[i] = s / 2147483648 - 1; }
  return buf;
}

export function loopNoise(ctx: BaseAudioContext, buf: AudioBuffer): AudioBufferSourceNode {
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  src.start();
  return src;
}

/** Engine voice interface shared by the procedural and the sample engine. */
export interface EngineVoice {
  readonly output: AudioNode;
  update(rpm: number, load: number, gain: number, time: number): void;
  stop(): void;
}

/**
 * Single-cylinder two-stroke: fires once per revolution. Sawtooth + square harmonics + sub, lowpass opened by
 * load, all amplitude-modulated at the firing frequency ("putt"), plus bandpassed exhaust noise.
 */
export class ProceduralEngine implements EngineVoice {
  readonly output: GainNode;
  private oscs: OscillatorNode[];
  private lfo: OscillatorNode;
  private lfoDepth: GainNode;
  private filter: BiquadFilterNode;
  private noiseBand: BiquadFilterNode;
  private noiseGain: GainNode;
  private noise: AudioBufferSourceNode;

  constructor(private ctx: BaseAudioContext, noise: AudioBuffer) {
    this.output = ctx.createGain();
    this.output.gain.value = 0;
    const am = ctx.createGain();
    am.gain.value = 0.6;
    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.Q.value = 2;
    const shaper = ctx.createWaveShaper();
    const curve = new Float32Array(1024);
    for (let i = 0; i < curve.length; i++) { const x = (i / (curve.length - 1)) * 2 - 1; curve[i] = Math.tanh(2.5 * x); }
    shaper.curve = curve;
    const mix: [OscillatorType, number, number][] = [['sawtooth', 1, 0.5], ['square', 2, 0.18], ['sine', 0.5, 0.45]];
    this.oscs = mix.map(([type, , level]) => {
      const o = ctx.createOscillator();
      o.type = type;
      const g = ctx.createGain();
      g.gain.value = level;
      o.connect(g).connect(shaper);
      o.start();
      return o;
    });
    shaper.connect(this.filter).connect(am).connect(this.output);

    this.lfo = ctx.createOscillator();
    this.lfo.type = 'sawtooth';
    this.lfoDepth = ctx.createGain();
    this.lfoDepth.gain.value = 0.4;
    this.lfo.connect(this.lfoDepth).connect(am.gain);
    this.lfo.start();

    this.noise = loopNoise(ctx, noise);
    this.noiseBand = ctx.createBiquadFilter();
    this.noiseBand.type = 'bandpass';
    this.noiseBand.Q.value = 0.9;
    this.noiseGain = ctx.createGain();
    this.noise.connect(this.noiseBand).connect(this.noiseGain).connect(am);
  }

  update(rpm: number, load: number, gain: number, time: number): void {
    const f = rpm / 60;
    const k = 0.03;
    this.oscs.forEach((o, i) => o.frequency.setTargetAtTime(f * [1, 2, 0.5][i]!, time, k));
    this.lfo.frequency.setTargetAtTime(f, time, k);
    this.lfoDepth.gain.setTargetAtTime(0.25 + 0.3 * load, time, k);
    this.filter.frequency.setTargetAtTime(300 + rpm * 0.12 + load * 2200, time, k);
    this.noiseBand.frequency.setTargetAtTime(600 + rpm * 0.15, time, k);
    this.noiseGain.gain.setTargetAtTime(0.12 + 0.3 * load, time, k);
    this.output.gain.setTargetAtTime(gain * (0.55 + 0.45 * load), time, k);
  }

  stop(): void {
    for (const o of [...this.oscs, this.lfo]) o.stop();
    this.noise.stop();
    this.output.disconnect();
  }
}

/** Short low thump + dirt crunch. */
export function playThump(ctx: BaseAudioContext, dest: AudioNode, noise: AudioBuffer, strength: number, t: number): void {
  const o = ctx.createOscillator();
  o.frequency.setValueAtTime(90, t);
  o.frequency.exponentialRampToValueAtTime(40, t + 0.25);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.9 * strength, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
  o.connect(g).connect(dest);
  o.start(t);
  o.stop(t + 0.4);
  playNoiseBurst(ctx, dest, noise, 'lowpass', 700, 0.5 * strength, 0.2, t);
}

export function playNoiseBurst(ctx: BaseAudioContext, dest: AudioNode, noise: AudioBuffer, type: BiquadFilterType,
  freq: number, level: number, decay: number, t: number): void {
  const src = ctx.createBufferSource();
  src.buffer = noise;
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  const g = ctx.createGain();
  g.gain.setValueAtTime(level, t);
  g.gain.exponentialRampToValueAtTime(0.001, t + decay);
  src.connect(f).connect(g).connect(dest);
  src.start(t, Math.random() * 1.5);
  src.stop(t + decay + 0.05);
}

/** A bird chirp: a short sine sweep. */
export function playChirp(ctx: BaseAudioContext, dest: AudioNode, t: number, pitch: number): void {
  const o = ctx.createOscillator();
  o.type = 'sine';
  o.frequency.setValueAtTime(pitch, t);
  o.frequency.exponentialRampToValueAtTime(pitch * 1.4, t + 0.06);
  o.frequency.exponentialRampToValueAtTime(pitch * 0.9, t + 0.12);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.08, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.13);
  o.connect(g).connect(dest);
  o.start(t);
  o.stop(t + 0.15);
}
